/** @jsx isolateComponent */
import { css } from '@emotion/core';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { Vars as TableVars } from './Vars';

type Props = {
  /**
   * The content to render within the component.
   */
  children?: React.ReactNode;
  /**
   * Styling hook.
   */
  className?: string;
  /**
   * Whether to freeze the cell during scrolling.
   */
  freeze?: 'row' | 'col' | 'row col';
  /**
   * Whether to render a shadow when the cell is frozen.
   */
  shadowed?: 'row' | 'col' | 'row col';
  /**
   * The `headers` attribute for `td`. Contains a space-separated list
   * of `id`s that reference `th` tags that describe `td`. Used for a11y.
   */
  headers?: string;
};

const Vars = createUniqueCustomProperties('TableTd', ['frozenCellZindex']);

// 🚨 Important! These styles are shared with `TableTh`. While this is
// generally frowned upon, it's fairly crucial to keep the styles between
// the components *nsync.
export const Styles = labelStyles('TableTd', {
  root: css`
    display: table-cell;
    vertical-align: middle;
    // For Safari
    text-align: initial;

    // tbody cells should always render lowest
    tbody &,
    tbody & {
      ${Vars.frozenCellZindex}: 1;
    }

    // thead cells should always render above tbody cells
    thead &,
    thead & {
      ${Vars.frozenCellZindex}: 2;
    }

    // thead cells that freeze in both directions should always
    // render at the very top
    thead &[data-freeze='row col'],
    thead &[data-freeze='row col'] {
      ${Vars.frozenCellZindex}: 3;
    }

    &[data-freeze~='col'] {
      position: sticky;
      left: 0;
      z-index: var(${Vars.frozenCellZindex}, 1);

      // Render the shadow on a frozen column
      &[data-shadowed~='col'] {
        &::before {
          pointer-events: none;
          content: '';
          display: block;
          position: absolute;
          width: 5px;
          top: 0;
          left: 100%;
          bottom: 0;
          opacity: var(${TableVars.frozenCellHorzShadowOpacity}, 0);
          transition: all 200ms ease-in-out;
          background: linear-gradient(
            90deg,
            rgba(0, 0, 0, 0.1),
            rgba(0, 0, 0, 0)
          );
        }
      }
    }

    &[data-freeze~='row'] {
      position: sticky;
      top: 0;
      z-index: var(${Vars.frozenCellZindex}, 1);

      // Render the shadow on a frozen row
      &[data-shadowed~='row'] {
        &::after {
          pointer-events: none;
          content: '';
          display: block;
          position: absolute;
          height: 5px;
          top: 100%;
          left: 0;
          width: 100%;
          opacity: var(${TableVars.frozenCellVertShadowOpacity}, 0);
          transition: all 200ms ease-in-out;
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.1),
            rgba(0, 0, 0, 0)
          );
        }
      }
    }
  `,
});

/**
 * An individual `td` table cell. 🚨 Note that this component shares a
 * lot of commonality with `TableTh`, however they have different purposes
 * and `Props` requirements so they're defined distinctly.
 */
export const TableTd = (props: Props) => {
  return (
    <td
      css={[Styles.root, props.className]}
      data-freeze={props.freeze}
      data-shadowed={props.shadowed}
      headers={props.headers}
      role="cell"
    >
      {props.children}
    </td>
  );
};
